import React, { useState, useEffect, useRef } from 'react';

import {
  Box,
  VStack,
  HStack,
  Text,
  Progress,
  SimpleGrid,
  Badge,
  Skeleton,
  Button,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';

import { formatDateTime } from 'utils/date.util';

import { useColors } from './colors';
import socket from '../../../app/socket';

const RunProgressSection = ({
  processStatus = null,
  onRefetchProcessStatus,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor } = useColors();
  const onRefetchRef = useRef(onRefetchProcessStatus);
  onRefetchRef.current = onRefetchProcessStatus;
  const hasHydratedFetchRef = useRef(false);

  const [fetchProgress, setFetchProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    status: 'idle',
    message: '',
  });

  const [autoSelectionProgress, setAutoSelectionProgress] = useState({
    current: 0,
    total: 0,
    percentage: 0,
    status: 'idle',
    message: '',
    activated: 0,
    skipped: 0,
    errors: 0,
  });

  const [isSectionVisible, setIsSectionVisible] = useState(false);

  // Hydrate from process-status once on load/refresh so we show most recent progress
  useEffect(() => {
    if (!processStatus?.fetchProgress || hasHydratedFetchRef.current) return;
    hasHydratedFetchRef.current = true;
    const p = processStatus.fetchProgress;
    setFetchProgress({
      current: p.current ?? 0,
      total: p.total ?? 0,
      percentage: p.percentage ?? 0,
      status: p.status ?? 'idle',
      message: p.message ?? '',
    });
  }, [processStatus]);

  useEffect(() => {
    socket.connect();

    socket.on('repricer-progress', (data) => {
      setFetchProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'running',
        message: data.message ?? '',
      });
    });

    socket.on('repricer-status', (data) => {
      if (data.isRunning) {
        setFetchProgress({
          current: 0,
          total: 0,
          percentage: 0,
          status: 'running',
          message: data.message ?? 'Starting optimization process...',
        });
      } else {
        setFetchProgress((prev) => ({
          ...prev,
          status: prev.percentage >= 100 ? 'completed' : prev.status,
          message: data.message ?? prev.message,
        }));
        if (onRefetchRef.current) onRefetchRef.current();
      }
    });

    socket.on('auto-selection-progress', (data) => {
      setAutoSelectionProgress({
        current: data.current ?? 0,
        total: data.total ?? 0,
        percentage: data.percentage ?? 0,
        status: data.status ?? 'idle',
        message: data.message ?? '',
        activated: data.activated ?? 0,
        skipped: data.skipped ?? 0,
        errors: data.errors ?? 0,
      });
      if (data.status === 'completed' && onRefetchRef.current) onRefetchRef.current();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const lastRun = processStatus?.lastRun;
  const isLoadingProcessStatus = processStatus === undefined;

  const getStatusColor = (status) => {
    if (status === 'completed') return 'green';
    if (status === 'idle') return 'gray';
    if (status === 'running') return 'blue';
    if (status === 'error') return 'red';
    return 'gray';
  };

  // Only show progress/status when running (or error); when completed or idle show idle state
  const isFetchActive = fetchProgress.status === 'running' || fetchProgress.status === 'error';
  const displayFetchProgress = isFetchActive
    ? fetchProgress
    : { current: 0, total: 0, percentage: 0, status: 'idle', message: '' };

  const isAutoSelectionActive =
    autoSelectionProgress.status === 'running' || autoSelectionProgress.status === 'error';
  const displayAutoSelectionProgress = isAutoSelectionActive
    ? autoSelectionProgress
    : {
        current: 0,
        total: 0,
        percentage: 0,
        status: 'idle',
        message: '',
        activated: 0,
        skipped: 0,
        errors: 0,
      };

  return (
    <Box mb={3}>
      <HStack mb={isSectionVisible ? 2 : 0} justify="space-between" align="center">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          leftIcon={isSectionVisible ? <FaChevronUp /> : <FaChevronDown />}
          onClick={() => setIsSectionVisible((prev) => !prev)}
          color={textColor}
        >
          {isSectionVisible ? 'Hide progress & last run' : 'Show progress & last run'}
        </Button>
      </HStack>

      {isSectionVisible && (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {/* Fetch status */}
          <Box
            p={5}
            bg={backgroundCardColor}
            borderRadius="lg"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Text color={textColor} fontSize="sm" fontWeight="600" mb={2}>
              Fetch status
            </Text>
            <VStack align="stretch" spacing={2} width="100%">
            <Box width="100%">
              <Progress
                value={displayFetchProgress.percentage}
                size="sm"
                width="100%"
                colorScheme={getStatusColor(displayFetchProgress.status)}
                borderRadius="full"
                hasStripe={displayFetchProgress.status === 'running'}
                isAnimated={displayFetchProgress.status === 'running'}
              />
            </Box>
            <HStack justify="space-between" fontSize="sm" color={secondaryTextColor}>
              <Text>
                {displayFetchProgress.current}/{displayFetchProgress.total}
                {displayFetchProgress.total > 0 ? ` (${displayFetchProgress.percentage}%)` : ''}
              </Text>
              <Badge
                size="sm"
                colorScheme={getStatusColor(displayFetchProgress.status)}
                variant="subtle"
              >
                {displayFetchProgress.status}
              </Badge>
            </HStack>
            {displayFetchProgress.message && (
              <Text fontSize="sm" color={secondaryTextColor} noOfLines={2}>
                {displayFetchProgress.message}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Auto selection status */}
        <Box
          p={5}
          bg={backgroundCardColor}
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Text color={textColor} fontSize="sm" fontWeight="600" mb={2}>
            Auto selection status
          </Text>
          <VStack align="stretch" spacing={2} width="100%">
            <Box width="100%">
              <Progress
                value={displayAutoSelectionProgress.percentage}
                size="sm"
                width="100%"
                colorScheme={getStatusColor(displayAutoSelectionProgress.status)}
                borderRadius="full"
                hasStripe={displayAutoSelectionProgress.status === 'running'}
                isAnimated={displayAutoSelectionProgress.status === 'running'}
              />
            </Box>
            <HStack justify="space-between" fontSize="sm" color={secondaryTextColor}>
              <Text>
                {displayAutoSelectionProgress.current}/{displayAutoSelectionProgress.total}
                {displayAutoSelectionProgress.total > 0
                  ? ` (${displayAutoSelectionProgress.percentage}%)`
                  : ''}
              </Text>
              <Badge
                size="sm"
                colorScheme={getStatusColor(displayAutoSelectionProgress.status)}
                variant="subtle"
              >
                {displayAutoSelectionProgress.status}
              </Badge>
            </HStack>
            {(displayAutoSelectionProgress.activated > 0 ||
              displayAutoSelectionProgress.skipped > 0 ||
              displayAutoSelectionProgress.errors > 0) && (
              <Text fontSize="sm" color={secondaryTextColor}>
                Activated: {displayAutoSelectionProgress.activated} · Skipped:{' '}
                {displayAutoSelectionProgress.skipped} · Errors: {displayAutoSelectionProgress.errors}
              </Text>
            )}
            {displayAutoSelectionProgress.message && (
              <Text fontSize="sm" color={secondaryTextColor} noOfLines={2}>
                {displayAutoSelectionProgress.message}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Last run details */}
        <Box
          p={5}
          bg={backgroundCardColor}
          borderRadius="lg"
          boxShadow="sm"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Text color={textColor} fontSize="sm" fontWeight="600" mb={2}>
            Last run
          </Text>
          <Skeleton isLoaded={!isLoadingProcessStatus} borderRadius="md">
            {lastRun ? (
              <VStack align="stretch" spacing={1} fontSize="sm">
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Status</Text>
                  <Badge
                    colorScheme={lastRun.status === 'completed' ? 'green' : 'red'}
                    variant="subtle"
                    fontSize="sm"
                  >
                    {lastRun.status}
                  </Badge>
                </HStack>
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Started</Text>
                  <Text color={textColor}>{formatDateTime(lastRun.startedAt)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Finished</Text>
                  <Text color={textColor}>{formatDateTime(lastRun.finishedAt)}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Listings fetched</Text>
                  <Text color={textColor}>{lastRun.listingsFetched ?? '—'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Successful</Text>
                  <Text color={textColor}>{lastRun.listingsSuccessful ?? '—'}</Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color={secondaryTextColor}>Chunks</Text>
                  <Text color={textColor}>{lastRun.chunksCreated ?? '—'}</Text>
                </HStack>
                {lastRun.error && (
                  <Text color="red.500" fontSize="sm" noOfLines={2}>
                    {lastRun.error}
                  </Text>
                )}
              </VStack>
            ) : (
              <Text color={secondaryTextColor} fontSize="sm">
                No run recorded yet.
              </Text>
            )}
          </Skeleton>
        </Box>
        </SimpleGrid>
      )}
    </Box>
  );
};

export default RunProgressSection;
